import Icon from '@Components/Icon'
import { alertsAtom } from '@Modules/hubs/atoms'
import { DANGER, INFO, SUCCESS, WARNING } from '@Stores/AlertStore/constants'
import React from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { HubParagraph } from 'src/common/rtl/HubWidgets'
import { RTLMainWrapper } from 'src/common/rtl/RTLWrapper'
import RTLWrapper from 'src/common/rtl/RTLWrapper'

export const Alerts = ({ dismiss, dir }) => {
  const alerts = useRecoilValue(alertsAtom)
  const style = dir === 'RTL' ? { left: 0 } : { right: 0 }

  return (
    <Container style={style}>
      <TransitionGroup>
        {alerts.map((alert) => (
          <CSSTransition classNames="alert" timeout={200} key={alert.id}>
            <Alert alert={alert} dismiss={dismiss} dir={dir} />
          </CSSTransition>
        ))}
      </TransitionGroup>
    </Container>
  )
}

export const Alert = ({ alert, dismiss, dir }) => {
  const onDismiss = () => {
    dismiss(alert.id)
  }

  const onRetry = () => {
    dismiss(alert.id)
    alert.retry()
  }

  const dismissOnTimeout = () => {
    setTimeout(onDismiss, 2000)
  }

  alert.type === 'SUCCESS' && dismissOnTimeout()

  const style = dir === 'RTL' ? { borderLeft: 'hidden' } : { borderRight: 'hidden' }

  return (
    <AlertContainer className={alert.type} style={style}>
      <AlertWrapper>
        <RTLWrapper>
          <Icon type={getIcon(alert)} />
        </RTLWrapper>
        <HubParagraph>{alert.message}</HubParagraph>
        {alert.retry && <HubParagraph onClick={onRetry}>Retry!</HubParagraph>}
        <AlertCloseIcon type={Icon.Types.close} size={Icon.Sizes.small} onClick={onDismiss} />
      </AlertWrapper>
    </AlertContainer>
  )
}

// :: Alert -> string|null
const getIcon = (alert) => {
  switch (alert.type) {
    case SUCCESS:
      return Icon.Types.check
    case DANGER:
      return Icon.Types.close
    case WARNING:
      return Icon.Types.warning
    case INFO:
      return Icon.Types.notify
    default:
      return Icon.Types.notify
  }
}

const Container = styled.div`
  position: fixed;
  bottom: 0;

  z-index: 100000;

  /* Transitions
   */
  .alert-enter {
    opacity: 0;
  }

  .alert-enter.alert-enter-active {
    opacity: 1;
    transition: opacity 200ms ease-out;
  }

  .alert-exit {
    opacity: 1;
  }

  .alert-exit.alert-exit-active {
    opacity: 0;
    transition: opacity 200ms ease-out;
  }
`

const AlertWrapper = styled(RTLMainWrapper)`
  display: flex;
  flex-direction: row;
  align-items: center;

  > .px-icon {
    margin-right: ${({ theme }) => theme.sizes.gutterSmall};
  }

  > p {
    color: white;
    margin: 0 ${({ theme }) => theme.sizes.gutterMedium};
    max-width: 300px;
    flex-grow: 1;
  }

  a {
    color: white;
  }
`

const AlertContainer = styled.div`
  border: none;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmall};
  margin: ${({ theme }) => theme.sizes.gutterSmall};
  padding: ${({ theme }) => theme.sizes.gutterMedium};
  min-width: 300px;

  &.${DANGER} .px-icon {
    color: ${({ theme }) => theme.colors.dangerColor};
  }
  &.${DANGER} {
    border-left: 5px solid ${({ theme }) => theme.colors.dangerColor};
    border-right: 5px solid ${({ theme }) => theme.colors.dangerColor};
  }

  &.${SUCCESS} .px-icon {
    color: ${({ theme }) => theme.colors.successColor};
  }
  &.${SUCCESS} {
    border-left: 5px solid ${({ theme }) => theme.colors.successColor};
    border-right: 5px solid ${({ theme }) => theme.colors.successColor};
  }

  &.${WARNING} .px-icon {
    color: ${({ theme }) => theme.colors.warningColor};
  }
  &.${WARNING} {
    border-left: 5px solid ${({ theme }) => theme.colors.warningColor};
    border-right: 5px solid ${({ theme }) => theme.colors.warningColor};
  }
`

export const AlertCloseIcon = styled(Icon)`
  margin-left: ${({ theme }) => theme.sizes.gutterSmall};
  color: ${({ theme }) => theme.colors.grayMedium};
`
